import * as Sentry from '@sentry/react';
import { API_URL } from 'constants/api';
import { sanitizeRequestMetadata } from './sentry-privacy';

type ApiRequestErrorKind = 'http' | 'network' | 'invalid_response';

export class ApiRequestError extends Error {
  constructor(
    public readonly kind: ApiRequestErrorKind,
    public readonly status?: number,
  ) {
    super(
      kind === 'http'
        ? `HTTP request failed with status ${status}`
        : kind === 'invalid_response'
        ? 'API response is invalid'
        : 'API request failed',
    );
    this.name = 'ApiRequestError';
    Object.setPrototypeOf(this, ApiRequestError.prototype);
  }
}

export const wrappedFetch = async (
  url: RequestInfo,
  options?: RequestInit,
): Promise<any> => {
  let status: number | undefined;
  try {
    const response = await fetch(url, options);
    status = response.status;

    if (!response.ok) {
      throw new ApiRequestError('http', status);
    }

    const text = await response.text();

    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch {
      throw new ApiRequestError('invalid_response', status);
    }
  } catch (error) {
    if (
      error &&
      typeof error === 'object' &&
      'name' in error &&
      error.name === 'AbortError'
    ) {
      throw error;
    }

    const safeError =
      error instanceof ApiRequestError
        ? error
        : new ApiRequestError('network', status);
    Sentry.captureException(safeError, {
      extra: sanitizeRequestMetadata({
        method:
          options?.method ?? (typeof url === 'string' ? 'GET' : url.method),
        status: safeError.status,
        kind: safeError.kind,
        online: navigator.onLine,
      }),
    });

    throw safeError;
  }
};

export { API_URL };
